import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, LucideIcon } from 'lucide-react';

export interface ComingSoonWatermarkProps {
  /**
   * Unique key for localStorage persistence (e.g., "compliance-dashboard")
   */
  pageKey: string;
  /**
   * Whether the watermark can be dismissed (default: true)
   */
  dismissible?: boolean;
  /**
   * Icon to display at the top of the card
   */
  icon: LucideIcon;
  /**
   * Title of the feature coming soon
   */
  title: string;
  /**
   * Detailed description of what features will be available
   */
  description: string;
}

/**
 * ComingSoonWatermark - A reusable watermark overlay component for pages under development
 * 
 * Displays a centered card with icon, title, and description for upcoming features.
 * The dismissal state is persisted via localStorage per page.
 */
export const ComingSoonWatermark: React.FC<ComingSoonWatermarkProps> = ({ 
  pageKey, 
  dismissible = true,
  icon: Icon,
  title,
  description
}) => {
  const storageKey = `watermark-dismissed-${pageKey}`;
  const [isDismissed, setIsDismissed] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(false);

  useEffect(() => {
    // Check localStorage on mount
    const dismissed = localStorage.getItem(storageKey);
    if (dismissed === 'true') {
      setIsDismissed(true);
    } else {
      // Fade in after a brief delay
      setTimeout(() => setIsVisible(true), 100);
    }
  }, [storageKey]);

  const handleDismiss = () => {
    setIsVisible(false);
    // Wait for fade-out animation to complete
    setTimeout(() => {
      setIsDismissed(true);
      localStorage.setItem(storageKey, 'true');
    }, 300);
  };

  if (isDismissed) {
    return null;
  }

  const modalContent = (
    <div
      className={`
        fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm
        flex items-center justify-center
        transition-opacity duration-300
        ${isVisible ? 'opacity-100' : 'opacity-0'}
      `}
      role="alert"
      aria-label="Page coming soon watermark"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
    >
      {/* Centered Card */}
      <div className="
        bg-neutral-900/95 border border-neutral-800
        rounded-lg shadow-2xl
        max-w-md w-full mx-4
        p-8
        text-center
        relative z-[101]
      ">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="
            w-20 h-20 rounded-2xl
            bg-gradient-to-br from-primary-500/20 to-cyan-500/20
            border-2 border-primary-500/30
            flex items-center justify-center
          ">
            <Icon className="w-10 h-10 text-primary-500" strokeWidth={1.5} />
          </div>
        </div>

        {/* Title */}
        <h2 className="
          text-2xl font-bold text-white mb-4
        ">
          {title}
        </h2>

        {/* Description */}
        <p className="
          text-neutral-400 leading-relaxed mb-6
        ">
          {description}
        </p>

        {/* Dismiss Button (if enabled) */}
        {dismissible && (
          <button
            onClick={handleDismiss}
            className="
              bg-primary-500 hover:bg-primary-600
              text-black font-medium
              px-6 py-2.5 rounded-lg
              transition-all duration-200
              shadow-lg hover:shadow-xl
              hover:scale-105
            "
            aria-label="Dismiss coming soon watermark"
          >
            Got it
          </button>
        )}
      </div>

      {/* Close X button in corner */}
      {dismissible && (
        <button
          onClick={handleDismiss}
          className="
            absolute top-4 right-4 z-[102]
            bg-neutral-900/90 hover:bg-neutral-800
            text-neutral-400 hover:text-white
            border border-neutral-800 hover:border-neutral-700
            rounded-lg p-2
            transition-all duration-200
            shadow-lg
            group
          "
          aria-label="Close watermark"
          title="Close"
        >
          <X className="w-5 h-5 group-hover:rotate-90 transition-transform duration-200" />
        </button>
      )}
    </div>
  );

  // Use portal to render at document root, bypassing any parent positioning
  return createPortal(modalContent, document.body);
};
